/**
 * shadcn-style Button. CVA variants match research-B §3.2 (default, primary,
 * secondary, ghost, dark, danger) trimmed to shadcn's standard names; the
 * "primary" variant in research is folded into "default" since the brand
 * accent IS the default fill.
 */
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  // Base — match prototype: 32px default height, 12.5px label, weight 500.
  // Inter is the body font (Q54). Focus ring uses --ring (warm accent).
  //
  // Touch-target (impeccable audit P2 / WCAG 2.5.5): on COARSE pointers
  // (touch / stylus) every button is floored to a 44px min-height via
  // `[@media(pointer:coarse)]:min-h-[44px]`. Input-method — NOT screen
  // size — is the trigger, so mouse/trackpad (`pointer: fine`) stays at
  // the deliberate 32px desktop density and renders pixel-identically.
  // The `icon` size additionally floors min-width (see below) since an
  // icon-only button has no label to give it width.
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-sm text-[12.5px] font-medium transition-colors duration-short focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0 [@media(pointer:coarse)]:min-h-[44px]',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-1 hover:bg-accent-ink',
        destructive: 'bg-destructive text-destructive-foreground shadow-1 hover:opacity-90',
        outline: 'border border-line bg-surface text-ink shadow-1 hover:bg-surface-2',
        secondary: 'bg-secondary text-secondary-foreground shadow-1 hover:opacity-90',
        ghost: 'text-ink hover:bg-surface-2',
        link: 'text-accent underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-7 px-2.5 text-xs',
        md: 'h-8 px-3.5',
        lg: 'h-10 px-5 text-sm',
        icon: 'h-8 w-8 [@media(pointer:coarse)]:min-w-[44px]',
        // T4.5 (R2 S-QA-3) — opt-in WCAG 2.1 AA touch-target variant
        // (≥44×44 CSS px). Used by mobile primary surfaces only; the
        // default size stays at 32px to preserve the existing visual
        // density on desktop. Callers must opt in explicitly via
        // `size="mobile"` — no global migration in this phase.
        mobile: 'h-11 px-4 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        // Default to type="button" so a Button inside a <form> doesn't accidentally submit.
        type={asChild ? undefined : (type ?? 'button')}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
